import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions, ActivityIndicator, Platform, Animated, Easing } from 'react-native';
import LinearGradient from '../../../../components/LinearGradient';
import { X, BookOpen, ClipboardCheck, Check } from '../../../../components/Icons';
import { useDispatch, useSelector } from 'react-redux';

import World from '../../../../components/World';
import { useRemScale } from '../../../../lib/remScale';
import { setCourseData, setCurrentLessonProgress } from '../../../../store/course';
import {
  resetLessonButtonInteraction,
  setLessonPieceText,
  setLessonStarted,
  setLessonPaused,
} from '../../../../store/meeting/actions/meeting';
import { enableWebboard } from '../../../../store/media';
import useLessonProgressUpdater from '../../../../hooks/useCourseProgressUpdater';
import { ASSESSMENT_BASE_URL } from 'config/app.config';

// CSS blur for web
const webBlurStyle = Platform.OS === 'web' ? {
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
} : {};

const PROGRESS_TEXT_DARK_SHADE = '#000000';
const PROGRESS_TEXT_LIGHT_SHADE = '#FFFFFF';
const QUIZ_TEXT_LIGHT_SHADE = '#FFFFFF';
const TEXT_SHADE_DARK = 'rgba(0,0,0,0.85)';
const TEXT_SHADE_LIGHT = 'rgba(255,255,255,0.85)';

const getProgressTextShadeStyle = (isDarkText) => ({
  textShadowColor: isDarkText ? TEXT_SHADE_LIGHT : TEXT_SHADE_DARK,
  textShadowOffset: { width: 0, height: 1 },
  textShadowRadius: 2,
});

/**
 * Lesson menu overlay – matches new-reference-ui-screens `LessonSelector.tsx` styling.
 * Shows lesson selection with Start Lesson and Start Quiz buttons.
 */
export default function LessonMenuOverlay({ onClose }) {
  const dispatch = useDispatch();
  const { rem } = useRemScale();
  const { width, height } = useWindowDimensions();
  const highlightBorderColor = 'rgba(197,232,54,0.30)';
  const [headerHeight, setHeaderHeight] = useState(0);
  const LESSON_MASTERY_THRESHOLD = 90;
  const LESSON_PROGRESS_COLOR = '#fbbf24';
  const QUIZ_PROGRESS_COLOR = '#8B5CF6';
  const QUIZ_BUTTON_GRADIENT = ['#A855F7', '#8B5CF6'];
  const [refreshingLessonId, setRefreshingLessonId] = useState(null);
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  const courseState = useSelector(state => state.course);
  const courseData = courseState?.courseData || {};
  const currentLessonProgress = courseState?.currentLessonProgress;
  const tokenUnitId = useSelector(state => state.app?.courseTokenValues?.unitId);
  const tokenUnitName = useSelector(state => state.app?.courseTokenValues?.unitName);
  const userId = useSelector(state => state.app?.courseTokenValues?.userId);
  const token = useSelector(state => state.meeting?.properties?.token);
  const loading = courseState?.loading;

  // Hook to update lesson progress in courseData when slides are viewed
  const { updateLessonProgressStates } = useLessonProgressUpdater();

  // Sync currentLessonProgress to courseData when slides are viewed
  useEffect(() => {
    console.log('[LessonMenuOverlay] currentLessonProgress changed:', {
      lessonId: currentLessonProgress?.currentLessonId,
      progressPercentage: currentLessonProgress?.currentLessonProgressPercentage,
      totalSlides: currentLessonProgress?.currentLessonTotalSlides,
      masteryScore: currentLessonProgress?.currentLessonMasteryScore,
    });
    if (
      currentLessonProgress?.currentLessonId &&
      currentLessonProgress?.currentLessonProgressPercentage !== undefined
    ) {
      console.log('[LessonMenuOverlay] Calling updateLessonProgressStates with:', {
        lessonId: currentLessonProgress.currentLessonId,
        newProgress: currentLessonProgress.currentLessonProgressPercentage,
        newLessonMasteryScore: currentLessonProgress.currentLessonMasteryScore || 0,
      });
      updateLessonProgressStates({
        lessonId: currentLessonProgress.currentLessonId,
        newProgress: currentLessonProgress.currentLessonProgressPercentage,
        newLessonMasteryScore: currentLessonProgress.currentLessonMasteryScore || 0,
      });
    }
  }, [currentLessonProgress]);

  const unit = useMemo(() => {
    const units = courseData?.units || [];
    if (!units.length) return null;

    const normalize = (s = '') => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    const getUnitId = (u) => u?.id ?? u?.unitId ?? u?.unitID ?? u?.unit_id ?? null;
    const getUnitTitle = (u) => u?.title ?? u?.qlUnit ?? '';

    let selected = null;
    if (tokenUnitId) {
      const target = normalize(tokenUnitId);
      selected = units.find(u => normalize(getUnitId(u)) === target) || null;
    }
    if (!selected && tokenUnitName) {
      const target = normalize(tokenUnitName);
      selected = units.find(u => normalize(getUnitTitle(u)) === target) || null;
    }
    if (!selected) selected = units[0] || null;

    if (selected && Array.isArray(selected.lessons)) {
      selected = {
        ...selected,
        lessons: selected.lessons.map(l => ({
          ...l,
          progress: typeof l?.progress === 'number' ? l.progress : (l?.progressValue ?? 0),
        })),
      };
    }
    if (selected) {
      selected = {
        ...selected,
        title: (selected.title || selected.qlUnit || ''),
        qlUnit: (selected.qlUnit || selected.title || ''),
        id: (selected.id ?? selected.unitId ?? selected.unitID ?? selected.unit_id),
      };
    }
    return selected;
  }, [courseData, tokenUnitId, tokenUnitName]);

  const lessons = useMemo(() => {
    const raw = unit?.lessons || [];
    return raw.map((l, idx) => {
      const lessonId = l?.lessonId ?? l?.id ?? idx + 1;
      const lessonName = l?.lessonName ?? l?.title ?? `Lesson ${idx + 1}`;
      const progress = typeof l?.progress === 'number' ? l.progress : (l?.progressValue ?? 0);
      return {
        ...l,
        lessonId,
        lessonName,
        progress,
      };
    });
  }, [unit]);

  const isLessonMastered = useCallback((lesson) => {
    if (lesson?.isMastered === true) return true;
    if (String(lesson?.masteryStatus || '').toUpperCase() === 'MASTERED') return true;
    const pct = Number(lesson?.lessonMasteryPercentage ?? 0);
    return Number.isFinite(pct) && pct >= LESSON_MASTERY_THRESHOLD;
  }, [LESSON_MASTERY_THRESHOLD]);

  const getQuizMasteryPct = useCallback((lesson) => {
    const n = Number(lesson?.lessonMasteryPercentage ?? 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }, []);

  const nowLessonId = useMemo(() => {
    const firstNotFullyMastered = lessons.find(l => getQuizMasteryPct(l) < 100);
    return firstNotFullyMastered?.lessonId ?? null;
  }, [lessons, getQuizMasteryPct]);

  const completedCount = lessons.filter(l => (l?.progress ?? 0) >= 100).length;
  const overallPct = lessons.length > 0 ? (completedCount / lessons.length) * 100 : 0;

  const masteredCount = useMemo(() => {
    return lessons.filter(isLessonMastered).length;
  }, [lessons, isLessonMastered]);
  const overallMasteryPct =
    lessons.length > 0 ? (masteredCount / lessons.length) * 100 : 0;

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const onStart = (event) => {
      const lessonId = event?.detail?.lessonId ?? null;
      setRefreshingLessonId(lessonId || '__any__');
    };
    const onEnd = () => {
      setRefreshingLessonId(null);
    };

    window.addEventListener('course:detailsRefresh:start', onStart);
    window.addEventListener('course:detailsRefresh:end', onEnd);
    return () => {
      window.removeEventListener('course:detailsRefresh:start', onStart);
      window.removeEventListener('course:detailsRefresh:end', onEnd);
    };
  }, []);

  useEffect(() => {
    if (!refreshingLessonId) return;
    const loop = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1200,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => {
      try {
        loop.stop();
      } catch (_) {}
      shimmerAnim.setValue(0);
    };
  }, [refreshingLessonId, shimmerAnim]);

  const unitTitle = unit?.title || unit?.qlUnit || '';
  const unitNumber = unit?.unitNumber ?? (
    Array.isArray(courseData?.units) && unit
      ? (courseData.units.findIndex(u => {
          const uId = u?.id ?? u?.unitId ?? u?.unitID ?? u?.unit_id;
          const unitId = unit?.id ?? unit?.unitId ?? unit?.unitID ?? unit?.unit_id;
          return String(uId) === String(unitId);
        }) + 1)
      : 1
  );

  const startLesson = (lesson) => {
    const idx = Math.max(0, Math.round(((lesson.progress || 0) / 100) * (lesson.totalSlides || 0)) - 1);
    dispatch(resetLessonButtonInteraction());
    dispatch(setLessonPieceText({ text: '' }));
    dispatch(setLessonStarted(true));
    dispatch(setLessonPaused(false));
    // If restarting the same lesson (especially after finishing), Unity can sometimes
    // stay in an "ended" state. Force a pause first, then re-init deterministically.
    const isRestartSameLesson =
      String(lesson?.lessonId) === String(currentLessonProgress?.currentLessonId);
    const isLastSlideRestart =
      isRestartSameLesson &&
      Number.isFinite(Number(lesson?.totalSlides)) &&
      Number(lesson.totalSlides) > 0 &&
      idx >= Number(lesson.totalSlides) - 1;

    // Unity sometimes fails to re-emit lesson piece text when restarting the SAME lessonId
    // (even though slides/video update). Switching to another lesson and back fixes it because
    // Unity fully re-initializes. Mimic that deterministically by briefly starting at slide 0,
    // then jumping to the intended slide, then resuming.
    const shouldDeterministicallyReinitSameLesson = isRestartSameLesson;

    // Bump lesson generation nonce to cancel any pending "exit lesson" timers that might still fire.
    const nonceAtSchedule = (() => {
      try {
        globalThis.__ql_lesson_nonce = (globalThis.__ql_lesson_nonce || 0) + 1;
        return globalThis.__ql_lesson_nonce || 0;
      } catch (_) {
        return 0;
      }
    })();

    // Do NOT force-pause here for same-lesson restarts; it can leave Unity stuck and never emitting text.

    const safeStartLesson = (slideIdx) => {
      try {
        // If another exit/start happened after this was scheduled, do nothing.
        if ((globalThis.__ql_lesson_nonce || 0) !== nonceAtSchedule) return;
      } catch (_) {}
      World.runCommand(`start_lesson ${lesson.lessonId} ${slideIdx}`);
    };

    const safeResumeLesson = () => {
      try {
        if ((globalThis.__ql_lesson_nonce || 0) !== nonceAtSchedule) return;
      } catch (_) {}
      try {
        // Bring Unity into an active lesson state so transcript/UI emits.
        World.runCommand('pause_lesson false');
        World.runCommand('process_lesson_input resume');
      } catch (_) {}
    };

    if (shouldDeterministicallyReinitSameLesson) {
      // Workaround: mimic "switch to other lesson and back" by forcing Unity to re-init the lesson.
      // Start at slide 0, then jump to the intended slide.
      setTimeout(() => {
        try {
          safeStartLesson(0);
        } catch (_) {}
        setTimeout(() => {
          safeStartLesson(idx);
          // After the second start, explicitly resume.
          setTimeout(safeResumeLesson, 120);
        }, 90);
      }, 60);
    } else {
      setTimeout(() => {
        safeStartLesson(idx);
        setTimeout(safeResumeLesson, 120);
      }, 50);
    }

    dispatch(setCourseData({ ...courseData, currentLessonId: lesson.lessonId, currentLessonTotalSlides: lesson.totalSlides }));
    dispatch(setCurrentLessonProgress({
      currentLessonId: lesson.lessonId,
      currentLessonProgressPercentage: lesson.progress,
      currentLessonTotalSlides: lesson.totalSlides,
      currentLessonMasteryScore: lesson.masteryScore,
    }));
    if (typeof onClose === 'function') onClose();
  };

  const startAssessment = (lesson) => {
    World.runCommand('pause_lesson true');
    dispatch(setCourseData({ ...courseData, currentLessonId: lesson.lessonId, currentLessonTotalSlides: lesson.totalSlides }));
    dispatch(setCurrentLessonProgress({
      currentLessonId: lesson.lessonId,
      currentLessonProgressPercentage: lesson.progress,
      currentLessonTotalSlides: lesson.totalSlides,
      currentLessonMasteryScore: lesson.masteryScore,
    }));
    // Open the assessment webboard
    const unitId = unit?.id ?? unit?.unitId ?? tokenUnitId;
    dispatch(
      enableWebboard(
        `${String(ASSESSMENT_BASE_URL || '').replace(/\/$/, '')}/?unit_id=${unitId}&lesson_id=${lesson.lessonId}&user_id=${userId}&token=${token}&currentAssessmentScore=${lesson.masteryScore || 0}`,
      ),
    );
    if (typeof onClose === 'function') onClose();
  };

  const cardWidth = Math.min(rem(320), width - rem(24) * 2);
  const cardMaxHeight = Math.round(height * 0.8);
  const listMaxHeight = Math.max(rem(120), cardMaxHeight - Math.max(0, headerHeight));

  return (
    <View style={styles.full} pointerEvents="box-none">
      {/* Blurred overlay background */}
      <View 
        style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(0,0,0,0.5)', ...webBlurStyle }]} 
      />

      {/* Center card */}
      <View style={[styles.card, { width: cardWidth, maxHeight: cardMaxHeight, borderRadius: rem(16) }]}>
        {/* Header */}
        <View
          onLayout={(e) => setHeaderHeight(e?.nativeEvent?.layout?.height || 0)}
          style={[styles.header, { padding: rem(16), borderBottomColor: 'rgba(255,255,255,0.10)' }]}
        >
          {/* Close button */}
          <TouchableOpacity
            onPress={onClose}
            style={{
              position: 'absolute',
              right: rem(12),
              top: rem(12),
              width: rem(24),
              height: rem(24),
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.7,
              backgroundColor: 'red',
              borderRadius: rem(6),
            }}
            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
            accessibilityLabel="Close"
          >
            <X size={rem(16)} color="#fff" />
          </TouchableOpacity>

          <Text style={{ color: '#fff', fontWeight: '700', fontSize: rem(18), marginBottom: rem(4) }}>
            Select Lesson
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.50)', fontSize: rem(12), marginBottom: rem(12) }}>
            {`Unit ${unitNumber}: ${unitTitle}`}
          </Text>

          {/* Lessons completed (completion) */}
          <View style={{ height: rem(20), borderRadius: rem(999), backgroundColor: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
            <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${overallPct}%`, backgroundColor: LESSON_PROGRESS_COLOR }} />
            <View style={styles.centerTextRow}>
              <Text
                style={{
                  fontSize: rem(10),
                  fontWeight: '700',
                  color: overallPct >= 50 ? PROGRESS_TEXT_DARK_SHADE : PROGRESS_TEXT_LIGHT_SHADE,
                  ...getProgressTextShadeStyle(overallPct >= 50),
                }}
              >
                {`${completedCount}/${lessons.length} lessons watched`}
              </Text>
            </View>
          </View>

          {/* Lessons mastered (mastery) */}
          <View style={{ marginTop: rem(10), height: rem(20), borderRadius: rem(999), backgroundColor: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
            <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${overallMasteryPct}%`, backgroundColor: QUIZ_PROGRESS_COLOR }} />
            <View style={styles.centerTextRow}>
              <Text
                style={{
                  fontSize: rem(10),
                  fontWeight: '700',
                  color: QUIZ_TEXT_LIGHT_SHADE,
                  ...getProgressTextShadeStyle(false),
                }}
              >
                {`${masteredCount}/${lessons.length} quizzes mastered`}
              </Text>
            </View>
          </View>
        </View>

        {/* Lessons List */}
        <ScrollView
          style={{ maxHeight: listMaxHeight }}
          contentContainerStyle={{ padding: rem(12) }}
          showsVerticalScrollIndicator={false}
        >
          {loading && lessons.length === 0 ? (
            <View style={{ padding: rem(12), alignItems: 'center' }}>
              <ActivityIndicator color="#C5E836" />
              <Text style={{ color: 'rgba(255,255,255,0.70)', fontSize: rem(12), textAlign: 'center', marginTop: rem(10) }}>
                Loading lessons…
              </Text>
            </View>
          ) : null}

          {!loading && lessons.length === 0 ? (
            <View style={{ padding: rem(12) }}>
              <Text style={{ color: 'rgba(255,255,255,0.70)', fontSize: rem(12), textAlign: 'center' }}>
                No lessons found for this unit yet.
              </Text>
            </View>
          ) : null}

          {lessons.map((lesson, idx) => {
            const progress = Math.max(0, Math.min(100, lesson?.progress ?? 0));
            const masteryPct = getQuizMasteryPct(lesson);
            const isCurrent = nowLessonId
              ? String(lesson?.lessonId) === String(nowLessonId)
              : false;
            const isLessonCompleted = progress >= 100;
            const isLessonMastered = masteryPct >= 100;
            const isFullyDone = isLessonCompleted && isLessonMastered;

            return (
              <View
                key={lesson.lessonId ?? idx}
                style={[
                  styles.lessonCard,
                  {
                    borderRadius: rem(12),
                    padding: rem(12),
                    marginBottom: rem(8),
                    backgroundColor: isCurrent ? 'rgba(197,232,54,0.10)' : 'rgba(255,255,255,0.05)',
                    borderColor: isCurrent ? highlightBorderColor : 'rgba(255,255,255,0.00)',
                  },
                ]}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  {/* Lesson number indicator */}
                  <View
                    style={{
                      width: rem(28),
                      height: rem(28),
                      borderRadius: rem(8),
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginRight: rem(12),
                      backgroundColor: isFullyDone
                        ? '#C5E836'
                        : isCurrent
                          ? 'rgba(197,232,54,0.20)'
                          : 'rgba(255,255,255,0.10)',
                      borderWidth: isFullyDone ? 0 : (isCurrent ? 1 : 0),
                      borderColor: isCurrent ? highlightBorderColor : 'transparent',
                    }}
                  >
                    {isFullyDone ? (
                      <Check size={rem(14)} color="#000" strokeWidth={3} />
                    ) : (
                      <Text style={{ color: isCurrent ? '#C5E836' : 'rgba(255,255,255,0.50)', fontSize: rem(12), fontWeight: '800' }}>
                        {idx + 1}
                      </Text>
                    )}
                  </View>

                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ color: '#fff', fontSize: rem(14), fontWeight: '500', lineHeight: rem(18) }}>
                      {lesson.lessonName || `Lesson ${idx + 1}`}
                    </Text>
                  </View>

                  {/* NOW badge */}
                  {isCurrent && (
                    <View style={{ paddingHorizontal: rem(6), paddingVertical: rem(2), borderRadius: rem(6), backgroundColor: 'rgba(239,68,68,0.20)', marginLeft: rem(8) }}>
                      <Text style={{ color: '#F87171', fontSize: rem(10), fontWeight: '700' }}>NOW</Text>
                    </View>
                  )}
                </View>

                {/* Lesson completed (completion) */}
                <View style={{ marginTop: rem(8), marginBottom: rem(8), height: rem(18), borderRadius: rem(999), backgroundColor: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
                  <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress}%`, backgroundColor: LESSON_PROGRESS_COLOR }} />
                  <View style={styles.centerTextRow}>
                    <Text
                      style={{
                        fontSize: rem(10),
                        fontWeight: '700',
                        color: progress >= 50 ? PROGRESS_TEXT_DARK_SHADE : PROGRESS_TEXT_LIGHT_SHADE,
                        ...getProgressTextShadeStyle(progress >= 50),
                      }}
                    >
                      {`${progress}% lesson watched`}
                    </Text>
                  </View>
                </View>

                {/* Lesson mastered (mastery) */}
                <View style={{ marginTop: rem(0), marginBottom: rem(10), height: rem(18), borderRadius: rem(999), backgroundColor: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
                  <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${masteryPct}%`, backgroundColor: QUIZ_PROGRESS_COLOR }} />
                  {(() => {
                    const shouldShimmer =
                      refreshingLessonId === '__any__' ||
                      (refreshingLessonId &&
                        String(refreshingLessonId) === String(lesson?.lessonId));
                    if (!shouldShimmer) return null;
                    const translateX = shimmerAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [-rem(60), rem(220)],
                    });
                    return (
                      <Animated.View
                        pointerEvents="none"
                        style={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          width: rem(80),
                          opacity: 0.35,
                          transform: [{ translateX }, { rotate: '-20deg' }],
                        }}
                      >
                        <LinearGradient
                          colors={[
                            'rgba(255,255,255,0.08)',
                            'rgba(255,255,255,0.55)',
                            'rgba(255,255,255,0.08)',
                          ]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                          style={{ flex: 1 }}
                        />
                      </Animated.View>
                    );
                  })()}
                  <View style={styles.centerTextRow}>
                    <Text
                      style={{
                        fontSize: rem(10),
                        fontWeight: '700',
                        color: QUIZ_TEXT_LIGHT_SHADE,
                        ...getProgressTextShadeStyle(false),
                      }}
                    >
                      {`${masteryPct}% quiz mastery`}
                    </Text>
                  </View>
                </View>

                {/* Action buttons */}
                <View style={{ flexDirection: 'row', gap: rem(8) }}>
                  <TouchableOpacity
                    activeOpacity={0.9}
                    onPress={() => startLesson(lesson)}
                    style={{ flex: 1, height: rem(32), borderRadius: rem(8), overflow: 'hidden' }}
                  >
                    <LinearGradient
                      colors={['#fbbf24', '#facc15']}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <BookOpen size={rem(14)} color="#000" strokeWidth={2.5} />
                      <Text style={{ marginLeft: rem(6), color: '#000', fontSize: rem(12), fontWeight: '700' }}>
                        Start Lesson
                      </Text>
                    </LinearGradient>
                  </TouchableOpacity>

                  <TouchableOpacity
                    activeOpacity={0.9}
                    onPress={() => startAssessment(lesson)}
                    style={{
                      flex: 1,
                      height: rem(32),
                      borderRadius: rem(8),
                      overflow: 'hidden',
                    }}
                  >
                    <LinearGradient
                      colors={QUIZ_BUTTON_GRADIENT}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      style={{ flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
                    >
                      <ClipboardCheck size={rem(14)} color="#fff" strokeWidth={2.5} />
                      <Text style={{ marginLeft: rem(6), color: '#fff', fontSize: rem(12), fontWeight: '700' }}>
                        Start Quiz
                      </Text>
                    </LinearGradient>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  full: { ...StyleSheet.absoluteFillObject, zIndex: 1000, justifyContent: 'center', alignItems: 'center', backgroundColor: 'transparent' },
  card: {
    backgroundColor: '#111216',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.6,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
  },
  header: {
    flexShrink: 0,
    borderBottomWidth: 1,
  },
  lessonCard: {
    borderWidth: 1,
  },
  centerTextRow: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
